// list-continuation.js - Auto-continue and renumber ordered lists
// Handles Enter key for list continuation and automatic renumbering

import { keymap } from "@codemirror/view";
import { EditorView } from "@codemirror/view";

// Check if a line is a numbered list item, return the number and content
function parseNumberedListItem(line) {
    const match = line.match(/^(\d+)\.\s(.*)$/);
    if (match) {
        return { number: parseInt(match[1], 10), content: match[2] };
    }
    return null;
}

// Find all numbered list items in a contiguous block starting from a line
function findListBlock(doc, startLineNum) {
    const lines = [];

    // Search backwards to find the start of the list
    let firstLineNum = startLineNum;
    for (let i = startLineNum - 1; i >= 1; i--) {
        const line = doc.line(i);
        if (parseNumberedListItem(line.text)) {
            firstLineNum = i;
        } else if (line.text.trim() === '') {
            if (i > 1) {
                const prevLine = doc.line(i - 1);
                if (!parseNumberedListItem(prevLine.text)) {
                    break;
                }
            }
        } else {
            break;
        }
    }

    // Collect all list items from the start
    for (let i = firstLineNum; i <= doc.lines; i++) {
        const line = doc.line(i);
        const parsed = parseNumberedListItem(line.text);
        if (parsed) {
            lines.push({ lineNum: i, from: line.from, to: line.to, ...parsed });
        } else if (line.text.trim() === '') {
            if (i < doc.lines) {
                const nextLine = doc.line(i + 1);
                if (!parseNumberedListItem(nextLine.text)) {
                    break;
                }
            }
        } else {
            break;
        }
    }

    return lines;
}

// Renumber a list block starting from a specific index
function renumberList(view, listItems, startIndex = 0) {
    if (listItems.length === 0) return;

    const changes = [];
    let expectedNumber = startIndex > 0 ? listItems[startIndex - 1].number + 1 : 1;

    for (let i = startIndex; i < listItems.length; i++) {
        const item = listItems[i];
        if (item.number !== expectedNumber) {
            const oldPrefix = `${item.number}. `;
            const newPrefix = `${expectedNumber}. `;
            changes.push({
                from: item.from,
                to: item.from + oldPrefix.length,
                insert: newPrefix
            });
        }
        expectedNumber++;
    }

    if (changes.length > 0) {
        view.dispatch({ changes });
    }
}

// Keymap extension to handle Enter key for list continuation
export const listContinuation = keymap.of([{
    key: "Enter",
    run: (view) => {
        const state = view.state;
        const selection = state.selection.main;

        // Only handle if no text is selected
        if (!selection.empty) return false;

        const cursorPos = selection.head;
        const line = state.doc.lineAt(cursorPos);
        const parsed = parseNumberedListItem(line.text);

        if (!parsed) return false;

        // Check if cursor is at the end of the line
        if (cursorPos !== line.to) return false;

        // If the list item is empty (just "N. "), remove it and exit list mode
        if (parsed.content.trim() === '') {
            view.dispatch({
                changes: { from: line.from, to: line.to, insert: '' }
            });
            return true;
        }

        // Insert new list item with next number
        const nextNumber = parsed.number + 1;
        const newLine = `\n${nextNumber}. `;

        view.dispatch({
            changes: { from: cursorPos, insert: newLine },
            selection: { anchor: cursorPos + newLine.length }
        });

        // Renumber subsequent items after a short delay
        setTimeout(() => {
            const newCursorPos = view.state.selection.main.head;
            const newLineObj = view.state.doc.lineAt(newCursorPos);
            const listItems = findListBlock(view.state.doc, newLineObj.number);

            const newItemIndex = listItems.findIndex(item => item.lineNum === newLineObj.number);
            if (newItemIndex >= 0 && newItemIndex < listItems.length - 1) {
                renumberList(view, listItems, newItemIndex + 1);
            }
        }, 0);

        return true;
    }
}]);

// Extension to renumber list when document changes
export const listAutoRenumber = EditorView.updateListener.of((update) => {
    if (!update.docChanged) return;

    // Check if a numbered list item was modified
    update.changes.iterChanges((fromA, toA, fromB, toB, inserted) => {
        const insertedText = inserted.toString();

        // Check if we inserted a numbered list pattern at the start of a line
        if (/^\d+\.\s/.test(insertedText)) {
            const line = update.state.doc.lineAt(fromB);
            const parsed = parseNumberedListItem(line.text);

            if (parsed) {
                setTimeout(() => {
                    const listItems = findListBlock(update.view.state.doc, line.number);
                    const currentIndex = listItems.findIndex(item => item.lineNum === line.number);

                    if (currentIndex >= 0) {
                        renumberList(update.view, listItems, currentIndex);
                    }
                }, 10);
            }
        }
    });
});
